// ════════════════════════════════════════════════════════════
// Entry point — ECR
// ════════════════════════════════════════════════════════════
/* eslint-disable react-refresh/only-export-components */

import './lib/dayjs';
import '@fontsource-variable/inter';
import '@fontsource-variable/jetbrains-mono';
import './styles/globals.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';

import { initSentry } from '@/lib/sentry';
import { queryClient } from '@/lib/queryClient';
import { bootstrapAuth } from '@/hooks/useAuth';
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt';
import { routeTree } from './routeTree.gen';

initSentry();

// Filet de sécurité cache PWA : si un import lazy échoue (un nouveau build a été
// déployé pendant la session → l'ancien chunk n'existe plus), on recharge pour
// récupérer les assets à jour. Garde-fou anti-boucle : 1 reload max / 10s.
window.addEventListener('vite:preloadError', () => {
  const key = 'pwa-chunk-reload-at';
  const last = Number(sessionStorage.getItem(key) ?? '0');
  if (Date.now() - last > 10_000) {
    sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
  }
});

const router = createRouter({
  // Le type de routeTree dépend de routeTree.gen.ts qui peut
  // ne pas être généré au moment du lint CI. Le runtime est OK.

  routeTree,
  defaultPreload: 'intent',
  context: {
    queryClient,
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found. Vérifier index.html');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <PWAUpdatePrompt />
        {!import.meta.env.PROD && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

void bootstrapAuth().catch((err: unknown) => {
  console.error('[Bootstrap] auth init failed:', err);
});

function ErrorFallback() {
  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'var(--font-sans, system-ui, sans-serif)',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Une erreur est survenue</h1>
      <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
        L&apos;erreur a été remontée à l&apos;équipe technique.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '0.5rem 1rem',
          background: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
        }}
      >
        Recharger la page
      </button>
    </div>
  );
}
